<template>
  <UiModal
    :is-shown="props.open"
    modal-class="modal-rubric"
    is-footer-hidden
    :title="props.title"
    :size="UiModalSize.M"
    is-header-fixed
    is-scrollbar-width-ignored
    is-full-height
    @close="closeModal"
  >
    <template #headerBottom>
      <div
        v-if="isSearchInputShown"
        class="rubric-search-block"
      >
        <SearchIcon class="rubrics-search-icon" />

        <UiInput
          :value="searchInput"
          is-full-width
          :type="UiInputTypes.TEXT"
          :placeholder="t('rubrics_popup.inputPlaceholder')"
          :class="searchInputClass"
          @focusin="handleSearchFieldClick"
          @input-value="updateSearch"
        />

        <ClearSearchIcon
          v-if="searchInput"
          class="rubric-search__close-icon"
          @click.stop="clearSearch"
        />
      </div>
    </template>
    <template #content>
      <RubricsPopupContent
        :rubrics-map="props.tags"
        :selected-rubric="props.selectedRubric"
        :default-sogu-fields="defaultSoguFields"
        :number-visible-tags="numberVisibleTags"
        :input-value="searchInput"
        :filter-query="props.filterQuery"
        @input="inputTagHandler"
        @select-rubric="selectedRubricHandle"
      />
    </template>
  </UiModal>
</template>

<script setup lang="ts">
  defineOptions({ inheritAttrs: false });

  import { UiModal, UiModalSize } from '@experience/core-modal-ui';
  import { UiInput, UiInputTypes } from '@experience/core-input-ui';
  import {
    type ListingTagMap,
    type ExtendedPartTagType,
    RubricsPopupContent,
    searchShownLimit,
  } from '@/entities/rubrics';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { trackEvent } from '@/entities/sogu';
  import { useScreenSize } from '@experience/shared';
  import { computed, ref } from 'vue';
  import type { LocationQueryRaw } from 'vue-router';
  import { ClearSearchIcon } from '@/shared/ui/icons/clearSearchIcon';
  import SearchIcon from '@/shared/ui/icons/SearchIcon.vue';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    open: boolean;
    tags: ListingTagMap[];
    title: string;
    selectedRubric: ExtendedPartTagType;
    defaultSoguFields: ContextGeoSubject;
    filterQuery?: LocationQueryRaw;
  }

  interface Emits {
    (e: 'close-modal'): void;
    (e: 'select-rubric', rubric: ExtendedPartTagType): void;
  }

  const countOfAllRubrics = computed(() => {
    return props.tags.reduce((acc, item) => {
      return acc + item.tags.length;
    }, 0);
  });

  const isSearchInputShown = computed(() => countOfAllRubrics.value > searchShownLimit);

  const searchInput = ref('');
  const searchInputClass = computed(() => [
    'rubric-search',
    {
      input_icon_right: searchInput.value,
    },
  ]);
  const handleSearchFieldClick = () => {
    trackEvent('listing:themes-popup:search.click', { ...props.defaultSoguFields.soguStamp });
  };
  const clearSearch = () => {
    searchInput.value = '';
  };
  const updateSearch = (value: string | number) => {
    searchInput.value = `${value}`;
  };

  const emits = defineEmits<Emits>();
  const props = defineProps<Props>();

  const { isMobile, isTablet } = useScreenSize();

  const numberVisibleTags = computed(() => {
    if (isMobile.value) {
      return 10;
    }

    if (isTablet.value) {
      return 24;
    }

    return 32;
  });

  const closeModal = () => {
    clearSearch();
    emits('close-modal');
  };

  const inputTagHandler = (params: { input: string; tag_id: string | undefined }) => {
    const payload = {
      ...props.defaultSoguFields.soguStamp,
      ...params,
    };
    trackEvent('listing:themes-popup.input', payload);
  };

  const selectedRubricHandle = (rubric: ExtendedPartTagType) => {
    clearSearch();
    emits('select-rubric', rubric);
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .rubric-search-block {
    position: relative;
    z-index: 1;
  }

  .rubric-search {
    width: 100%;
    padding-top: 24px;
    background: var(--comp-input-color-background);
  }

  .rubric-search:deep(.input__inner-wrapper) {
    padding-left: 44px;
  }

  .rubric-search.input_icon_right:deep(.input__inner-wrapper) {
    padding-right: 44px;
  }

  .rubric-search:deep(input) {
    font-size: var(--global-typography-size-400) !important;
    text-overflow: ellipsis;
  }

  .rubrics-search-icon {
    position: absolute;
    top: 38px;
    bottom: 0;
    z-index: 3;
    display: flex;
    width: 48px;
    pointer-events: none;
  }

  .rubric-search__close-icon {
    position: absolute;
    top: 38px;
    right: 12px;
    z-index: 99;
    cursor: pointer;
  }
</style>
